import { useCallback, useEffect, useState } from "react";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import Typography from "@mui/material/Typography";
import { LoadingOverlay } from "@covertree/ui";

import { BASE_RESIDENTS_PATH } from "@routing/Residents";

import { GetCTCoverageDetailsResponseStatus } from "@graphql/api";
import { CoverageType } from "@enums";
import { CoverageTypeFormData } from "@forms";

import useAppDispatch from "@redux/hooks/useAppDispatch";
import { useGetCtCoverageDetailsMutation } from "@redux/coverages/coveragesApi";
import {
  resetExternalCoverageData,
  setCtPolicyLocator,
} from "@redux/coverages/coveragesSlice";

import AddressMismatch from "./AddressMismatch/AddressMismatch";
import PolicyTypeForm from "./PolicyTypeForm";
import {
  BASE_COVERAGE_DATA_PATH,
  COVERAGE_DATA_ROUTES,
} from "@routing/Residents/ProofOfCoverage/CoverageData";
import { PROOF_OF_COVERAGE_ROUTES } from "@routing/Residents/ProofOfCoverage";
import { moveBackInPathname } from "@utils";

export default function CoverageTypeForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const { residentId } = useParams();

  const [openAddressMismatchModal, setOpenAddressMismatchModal] =
    useState<boolean>(false);

  const [getCtCoverageDetails, { data, isSuccess, isLoading, isError }] =
    useGetCtCoverageDetailsMutation();

  const submit = async (form: CoverageTypeFormData) => {
    if (form.coverageType === CoverageType.Covertee) {
      dispatch(setCtPolicyLocator(form.policyId));
      getCtCoverageDetails({
        residentId,
        policyLocator: form.policyId,
      });
    } else {
      dispatch(resetExternalCoverageData());
      navigate({
        pathname: `${moveBackInPathname(location.pathname, 1)}/${PROOF_OF_COVERAGE_ROUTES.POLICY_DOCUMENT}`,
        search: createSearchParams(searchParams).toString(),
      });
    }
  };

  const onClose = useCallback(() => {
    if (residentId) {
      navigate({
        pathname: moveBackInPathname(location.pathname, 2),
        search: createSearchParams(searchParams).toString(),
      });
    } else {
      navigate(BASE_RESIDENTS_PATH);
    }
  }, [navigate, residentId, searchParams]);

  useEffect(() => {
    if (
      isSuccess &&
      data.status === GetCTCoverageDetailsResponseStatus.AddressMismatch
    ) {
      setOpenAddressMismatchModal(true);
    }
  }, [isSuccess, data]);

  if (isLoading) return <LoadingOverlay />;

  if (!residentId)
    return (
      <Typography>
        An error occurred. Please check your internet connection or contact our
        team.
      </Typography>
    );

  if (isError)
    return (
      <Typography>
        Could not get any data for CT policy ID. Please check your internet
        connection or contact our team.
      </Typography>
    );

  if (
    isSuccess &&
    data.status === GetCTCoverageDetailsResponseStatus.PolicyNotFound
  ) {
    navigate(
      {
        pathname: `${moveBackInPathname(location.pathname, 1)}/${BASE_COVERAGE_DATA_PATH}/${COVERAGE_DATA_ROUTES.POLICY_NOT_FOUND}`,
        search: createSearchParams(searchParams).toString(),
      },
      { relative: "path" }
    );
    return <LoadingOverlay />;
  }

  if (
    isSuccess &&
    data.status === GetCTCoverageDetailsResponseStatus.PolicyAlreadyAdded
  ) {
    navigate(
      {
        pathname: `${moveBackInPathname(location.pathname, 1)}/${BASE_COVERAGE_DATA_PATH}/${COVERAGE_DATA_ROUTES.POLICY_ALREADY_ADDED}`,
        search: createSearchParams(searchParams).toString(),
      },
      { relative: "path" }
    );
    return <LoadingOverlay />;
  }

  if (isSuccess && data.status === GetCTCoverageDetailsResponseStatus.Success) {
    navigate(
      {
        pathname: `${moveBackInPathname(location.pathname, 1)}/${BASE_COVERAGE_DATA_PATH}/${COVERAGE_DATA_ROUTES.COVERAGE_DETAILS}`,
        search: createSearchParams(searchParams).toString(),
      },
      { relative: "path", replace: true }
    );
    return <LoadingOverlay />;
  }

  return (
    <>
      <PolicyTypeForm onSubmit={submit} onClose={onClose} />
      {openAddressMismatchModal && (
        <AddressMismatch
          residentSiteAddress={data.residentSiteAddress}
          policyUnitAddress={data.policyUnitAddress}
          open={openAddressMismatchModal}
          onClose={() => {
            setOpenAddressMismatchModal(false);
          }}
        />
      )}
    </>
  );
}
